/** Shared lightable-color sanitize for sports presets and Jules AI palettes. */

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export const MIN_LIGHTING_LUMINANCE = 42;
export const MIN_LIGHTING_CHANNEL = 40;

export function relativeLuminance(r, g, b) {
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isNearBlack(r, g, b) {
	return Math.max(r, g, b) < 25;
}

export function isLightableColor(r, g, b) {
	if (Math.max(r, g, b) < MIN_LIGHTING_CHANNEL) return false;
	return relativeLuminance(r, g, b) >= MIN_LIGHTING_LUMINANCE;
}

function rgbToHsv255(r, g, b) {
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const d = max - min;
	let h = 0;
	if (d !== 0) {
		switch (max) {
			case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
			case g: h = ((b - r) / d + 2) / 6; break;
			default: h = ((r - g) / d + 4) / 6; break;
		}
	}
	const s = max === 0 ? 0 : d / max;
	const v = max;
	return { h: h * 360, s: s * 100, v: v * 100 };
}

function hsv255ToRgb(h, s, v) {
	h = ((h % 360) + 360) % 360;
	s = clamp(s, 0, 100) / 100;
	v = clamp(v, 0, 100) / 100;
	const c = v * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = v - c;
	let r = 0;
	let g = 0;
	let b = 0;
	if (h < 60) { r = c; g = x; }
	else if (h < 120) { r = x; g = c; }
	else if (h < 180) { g = c; b = x; }
	else if (h < 240) { g = x; b = c; }
	else if (h < 300) { r = x; b = c; }
	else { r = c; b = x; }
	return [
		clamp(Math.round((r + m) * 255), 0, 255),
		clamp(Math.round((g + m) * 255), 0, 255),
		clamp(Math.round((b + m) * 255), 0, 255),
	];
}

/** Lock hue; raise saturation, then value — never wash out toward white. */
export function toLightableTuple(tuple) {
	const [r, g, b] = tuple;
	if (isNearBlack(r, g, b)) return null;
	if (isLightableColor(r, g, b)) return tuple;

	const { h, s, v } = rgbToHsv255(r, g, b);

	for (let sat = s; sat <= 100; sat += 1) {
		const boosted = hsv255ToRgb(h, sat, v);
		if (isLightableColor(...boosted)) return boosted;
	}

	for (let val = v; val <= 100; val += 1) {
		const boosted = hsv255ToRgb(h, 100, val);
		if (isLightableColor(...boosted)) return boosted;
	}

	return hsv255ToRgb(h, 100, 100);
}

function variantTuple(tuple, pass) {
	const { h, s, v } = rgbToHsv255(...tuple);
	const sat = clamp(s + (pass % 4) * 4, 0, 100);
	const val = clamp(v + Math.floor(pass / 4) * 4, 0, 100);
	return hsv255ToRgb(h, sat, val);
}

function tuplesEqual(a, b) {
	return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/** Drop black / very dark neutrals; boost dark brand swatches via saturation/value. */
export function sanitizeLightableTuples(tuples) {
	if (!Array.isArray(tuples) || tuples.length === 0) return [];

	const processed = tuples.map(toLightableTuple).filter(Boolean);
	const bases = processed.length > 0
		? processed
		: [toLightableTuple(
			tuples.reduce(
				(best, t) => (relativeLuminance(...t) > relativeLuminance(...best) ? t : best),
				tuples[0]
			),
		)].filter(Boolean);

	if (bases.length === 0) return [];

	const out = [...bases];
	let pass = 0;
	while (out.length < 4 && pass < 12) {
		const variant = variantTuple(bases[pass % bases.length], pass + 1);
		if (isLightableColor(...variant) && !out.some((t) => tuplesEqual(t, variant))) {
			out.push(variant);
		}
		pass++;
	}
	return out.slice(0, Math.max(4, tuples.length));
}

function toTuple(color) {
	if (Array.isArray(color) && color.length >= 3) {
		return [
			clamp(Math.round(Number(color[0]) || 0), 0, 255),
			clamp(Math.round(Number(color[1]) || 0), 0, 255),
			clamp(Math.round(Number(color[2]) || 0), 0, 255),
		];
	}
	if (color && typeof color === 'object') {
		return [
			clamp(Math.round(Number(color.r) || 0), 0, 255),
			clamp(Math.round(Number(color.g) || 0), 0, 255),
			clamp(Math.round(Number(color.b) || 0), 0, 255),
		];
	}
	return null;
}

/**
 * Sanitize AI / freeform RGB colors into lightable {r,g,b} objects.
 * Pads or trims to numZones when provided.
 */
export function sanitizeRgbColors(colors, numZones) {
	const tuples = (colors || []).map(toTuple).filter(Boolean);
	const sanitized = sanitizeLightableTuples(tuples);
	const rgb = sanitized.map(([r, g, b]) => ({ r, g, b }));
	if (!numZones || numZones <= 0) return rgb;
	if (rgb.length === 0) {
		return Array.from({ length: numZones }, () => ({ r: 128, g: 128, b: 128 }));
	}
	const out = [];
	for (let i = 0; i < numZones; i++) {
		out.push({ ...rgb[i % rgb.length] });
	}
	return out;
}
